import type { LogoutOptions } from '@auth0/auth0-spa-js';
import type { AxiosInstance } from 'axios';
import React from 'react';

import { toClientDetailsHeader } from '@peloton/analytics/clientDetailsHeader';
import type { LogoutOptions as PLogoutOptions } from '@peloton/auth/redux';
import { deAuthenticate } from '@peloton/auth/redux';
import type { ApiEnvs } from '@peloton/external-links/models';
import { toWWWLink, toHref, toExtLinkEnv } from '@peloton/external-links/models';

import { toLocaleFromHostname } from '@peloton/internationalize/models/locale';
import { onAuthError, onLogoutEvent } from '@auth/monitoring';
import AuthenticationGuard from '@auth/oauth/AuthenticationGuard';
import { useAuth } from '@auth/oauth/AuthProvider';
import { useLogout as useAuthLogout } from '@auth/oauth/useLogout';
import { logoutPost } from '@engage/api-v2/generated';
import { getOauthFragment } from '@engage/auth/oauthFragment';
import { useReduxAction } from '@engage/redux/hooks';

import { useUtmParams } from '@members/analytics/helpers/hooks';
import MembersAuthProvider from '@members/auth/MembersAuthProvider';
import {
  engageApi,
  engageApiTextResponseType,
  presenceApi,
  pagesApi,
  graphQlApi,
  tooltipsApi,
  guidanceApi,
  progressTrackingApi,
  contentPreferencesApi,
  userPreferencesApi,
  clubPelotonApi,
} from '@members/data-fetch/singletons';
import useAuthorizationHeader from '@members/data-fetch/useAuthorizationHeader';
import { useFeatureToggle } from '@members/feature-toggles/featureToggles';
import { Feature } from '@members/feature-toggles/toggles';
import { useMembersRouter as useRouter } from '@members/localization/useMembersRouter';
import { captureClubPelotonDeeplinkIntent } from '@members/pg-overview/ClubPeloton/deeplinkIntent';
import { useProspectsRedirect } from '@prospects/guest-pass/hooks/useProspectsRedirect';
import { storeLoginLocale } from './loginLocalePersistence';

// Public, unauthenticated-accessible routes that must render even though the
// AuthenticationGuard below requires auth — the guard's own default bypass
// list only covers OAuth callback routes.
const MEMBERS_BYPASS_AUTH_PATHS: readonly string[] = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/reactivate-account',
  // Browser fallback for app-only workout-control deep links (TRAFFIC-1966).
  '/workout-control',
];

const MembersAuthGuard: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { query } = useRouter();
  const { isAuthenticated } = useAuth();

  // Persist a Club Peloton modal deep-link (`?modal=clubPeloton`) the moment a
  // *logged-out* member hits the link, before AuthenticationGuard fires the
  // Auth0 login redirect. The query param has to survive a multi-hop chain
  // (deep link → Auth0 → /callback → hard navigation via location.assign) to
  // re-open the modal post-login, and QA found it only survived for some
  // locales. Persisting the intent here — and restoring it on the overview
  // page after login (see pg-overview/Overview) — makes the deep link behave
  // identically for every locale, independent of how the redirect chain
  // handles the query. No-op when the param is absent, and gated on
  // `!isAuthenticated` so an already-authed member opening the modal never
  // writes a marker that could cause a stale re-open.
  React.useEffect(() => {
    if (!isAuthenticated) {
      captureClubPelotonDeeplinkIntent(
        typeof window === 'undefined' ? '' : window.location.search,
      );
    }
  }, [isAuthenticated]);

  const isProspectsGuestPassEnabled = useFeatureToggle(
    Feature.IsProspectsGuestPassEnabled,
  );
  const prospectsRedirect = useProspectsRedirect(
    typeof window === 'undefined' ? '' : window.location.search,
    isProspectsGuestPassEnabled,
  );
  const canRedirectToProspects =
    prospectsRedirect !== null && isProspectsGuestPassEnabled;
  const utmParams = useUtmParams();

  return (
    <AuthenticationGuard
      requiresAuth={true}
      bypassAuthPaths={MEMBERS_BYPASS_AUTH_PATHS}
      loginRedirectOverride={() =>
        canRedirectToProspects
          ? toHref(toWWWLink(prospectsRedirect!), toExtLinkEnv())
          : undefined
      }
      loginOptions={() => {
        // Persist the current locale so Callback.tsx can build a locale-aware
        // fallback redirect when appState.returnTo is absent (edge cases such
        // as session-state errors or Auth0 partial failures).
        storeLoginLocale(toLocaleFromHostname());
        return {
          fragment: JSON.stringify(getOauthFragment(query, '', toLocaleFromHostname())),
          authorizationParams: { ...utmParams },
        };
      }}
    >
      {children}
    </AuthenticationGuard>
  );
};

const WaitForInitializationWithHeaders: React.FC<
  React.PropsWithChildren<{ reduxClient: AxiosInstance }>
> = ({ reduxClient, children }) => {
  const { isLoading } = useAuth();

  useAuthorizationHeader({
    engageApi: engageApi.axios,
    engageApiTextResponseType: engageApiTextResponseType.axios,
    presenceApi: presenceApi.axios,
    pagesApi: pagesApi.axios,
    graphQlApi: graphQlApi.axios,
    tooltipsApi: tooltipsApi.axios,
    guidanceApi: guidanceApi.axios,
    progressTrackingApi: progressTrackingApi.axios,
    contentPreferencesApi: contentPreferencesApi.axios,
    userPreferencesApi: userPreferencesApi.axios,
    clubPelotonApi: clubPelotonApi.axios,
    reduxClient,
  });
  // Wait for Oauth to be initialized before rendering providers/components that will request data
  return <>{isLoading ? null : children}</>;
};

const OauthProvider: React.FC<
  React.PropsWithChildren<{
    apiEnv: ApiEnvs;
    reduxClient: AxiosInstance;
  }>
> = props => (
  <MembersAuthProvider apiEnv={props.apiEnv}>
    <WaitForInitializationWithHeaders reduxClient={props.reduxClient}>
      <MembersAuthGuard>{props.children}</MembersAuthGuard>
    </WaitForInitializationWithHeaders>
  </MembersAuthProvider>
);

export default OauthProvider;

export const useLogout = () => {
  const authLogout = useAuthLogout({
    fallbackReturnTo: window.location.origin,
    onLogoutEvent,
    onError: onAuthError,
  });
  const deauth = useReduxAction(deAuthenticate);

  return async (
    options: PLogoutOptions = {},
    oauthLogoutOptions: LogoutOptions = {},
    shouldLogoutLegacy: boolean = true,
  ) => {
    if (shouldLogoutLegacy) {
      try {
        await logoutPost(engageApi, toClientDetailsHeader(options));
        deauth();
      } catch (e) {
        // if we fail to logout of legacy, continue to oauth logout anyways
      }
    }

    const returnTo = oauthLogoutOptions.logoutParams?.returnTo;
    await authLogout(typeof returnTo === 'string' ? returnTo : window.location.href);
  };
};
